import { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { User, Mail, Phone, Globe, Linkedin, Calendar, UserCheck, Download, MessageSquare, Trash2, Send, X, FileDown, GitBranch, Video, Building2, Star, Plus } from "lucide-react";
import { recruitmentAPI, careersAPI } from "../services/api";

const STAGE_COLORS = {
  new: "#5e6ad2", screening: "#d97706", interview: "#7c3aed",
  offer: "#0284c7", hired: "#16a34a", rejected: "#dc2626",
};

const downloadCSV = async () => {
  const res = await recruitmentAPI.exportCSV();
  const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8;" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `candidatures_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  window.URL.revokeObjectURL(url);
};

const EMPTY_CANDIDATE_FORM = { first_name: "", last_name: "", email: "", phone: "", source: "direct", job_posting_id: "" };

function AddCandidateModal({ onClose, postings }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_CANDIDATE_FORM);
  const [error, setError] = useState("");
  const qc = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (data) => {
      const candidate = await recruitmentAPI.createCandidate({
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        phone: data.phone || undefined,
        source: data.source,
        gdpr_consent: true,
      });
      if (data.job_posting_id) {
        await recruitmentAPI.createApplication({
          candidate_id: candidate.data.id,
          job_posting_id: Number(data.job_posting_id),
        });
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["applications", "all"] });
      onClose();
    },
    onError: (err) => setError(err?.response?.data?.detail || t("candidates.add.createError")),
  });

  const field = (name, label, type = "text", required = false) => (
    <div>
      <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{label}{required && " *"}</label>
      <input
        type={type}
        value={form[name]}
        onChange={(e) => setForm(f => ({ ...f, [name]: e.target.value }))}
        required={required}
        className="w-full px-3 py-2 rounded-lg text-sm"
        style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: "rgba(0,0,0,0.4)" }}>
      <div className="rounded-xl p-6 w-full max-w-md shadow-xl" style={{ backgroundColor: "var(--lm-bg)", border: "1px solid var(--lm-border)" }}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold" style={{ color: "var(--lm-ink)" }}>{t("candidates.add.title")}</h3>
          <button onClick={onClose}><X className="h-4 w-4" style={{ color: "var(--lm-ink-3)" }} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); setError(""); mutation.mutate(form); }} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            {field("first_name", t("candidates.add.firstName"), "text", true)}
            {field("last_name", t("candidates.add.lastName"), "text", true)}
          </div>
          {field("email", t("candidates.add.email"), "email", true)}
          {field("phone", t("candidates.add.phone"))}
          <div>
            <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("candidates.add.source")}</label>
            <select value={form.source} onChange={(e) => setForm(f => ({ ...f, source: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}>
              <option value="direct">{t("candidates.add.sources.direct")}</option>
              <option value="linkedin">{t("candidates.add.sources.linkedin")}</option>
              <option value="indeed">{t("candidates.add.sources.indeed")}</option>
              <option value="referral">{t("candidates.add.sources.referral")}</option>
              <option value="other">{t("candidates.add.sources.other")}</option>
            </select>
          </div>
          {postings.length > 0 && (
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("candidates.add.linkPosting")}</label>
              <select value={form.job_posting_id} onChange={(e) => setForm(f => ({ ...f, job_posting_id: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}>
                <option value="">{t("candidates.add.noPosting")}</option>
                {postings.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
              </select>
            </div>
          )}
          {error && <p className="text-xs" style={{ color: "#e5484d" }}>{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm"
              style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}>
              {t("common.cancel")}
            </button>
            <button type="submit" disabled={mutation.isPending} className="px-4 py-2 rounded-lg text-sm font-medium text-white"
              style={{ backgroundColor: "#5e6ad2", opacity: mutation.isPending ? 0.6 : 1 }}>
              {mutation.isPending ? t("candidates.add.creating") : t("candidates.add.create")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function CandidatsFiche() {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [selectedApp, setSelectedApp] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const { data: applications = [], isLoading } = useQuery({
    queryKey: ["applications", "all"],
    queryFn: () => recruitmentAPI.listApplications({ limit: 200 }).then((r) => r.data),
  });

  const { data: postings = [] } = useQuery({
    queryKey: ["job-postings", "published"],
    queryFn: () => recruitmentAPI.listPostings({ status: "published", limit: 100 }).then((r) => r.data),
  });

  const filtered = applications.filter((app) => {
    if (!search) return true;
    const q = search.toLowerCase();
    const c = app.candidate;
    if (!c) return false;
    return (
      c.first_name?.toLowerCase().includes(q) ||
      c.last_name?.toLowerCase().includes(q) ||
      c.email?.toLowerCase().includes(q)
    );
  });

  const selected = selectedApp ? applications.find((a) => a.id === selectedApp) : null;

  return (
    <>
    {showAddModal && <AddCandidateModal onClose={() => setShowAddModal(false)} postings={postings} />}
    <div className="flex gap-4" style={{ minHeight: "400px" }}>
      {/* Left — list */}
      <div className="flex flex-col gap-2" style={{ width: "320px", flexShrink: 0 }}>
        <div className="flex gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("candidates.search")}
            className="px-3 py-2 rounded-lg text-sm flex-1 min-w-0"
            style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}
          />
          <button
            onClick={() => setShowAddModal(true)}
            title={t("candidates.newCandidate")}
            className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg transition-colors"
            style={{ backgroundColor: "#5e6ad2", color: "white" }}
          >
            <Plus className="h-4 w-4" />
          </button>
          <button
            onClick={downloadCSV}
            title={t("candidates.exportCsv")}
            className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg transition-colors"
            style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink-3)" }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(94,106,210,0.4)"; e.currentTarget.style.color = "#5e6ad2"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--lm-border)"; e.currentTarget.style.color = "var(--lm-ink-3)"; }}
          >
            <FileDown className="h-4 w-4" />
          </button>
        </div>
        <div className="overflow-y-auto flex flex-col gap-1.5" style={{ maxHeight: "500px" }}>
          {isLoading ? (
            <p className="text-xs text-center py-8" style={{ color: "var(--lm-ink-3)" }}>{t("common.loading")}</p>
          ) : filtered.length === 0 ? (
            <p className="text-xs text-center py-8" style={{ color: "var(--lm-ink-3)" }}>{t("candidates.empty")}</p>
          ) : (
            filtered.map((app) => {
              const c = app.candidate;
              const name = c ? `${c.first_name} ${c.last_name}` : `#${app.id}`;
              const st = { color: STAGE_COLORS[app.stage] || STAGE_COLORS.new, label: t(`recruitment.stages.${STAGE_COLORS[app.stage] ? app.stage : "new"}`) };
              const isSelected = selectedApp === app.id;

              return (
                <button
                  key={app.id}
                  onClick={() => setSelectedApp(isSelected ? null : app.id)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-left w-full transition-all"
                  style={{
                    backgroundColor: isSelected ? "rgba(94,106,210,0.10)" : "var(--lm-s1)",
                    border: isSelected ? "1px solid rgba(94,106,210,0.35)" : "1px solid var(--lm-border)",
                  }}
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold"
                    style={{ backgroundColor: "rgba(94,106,210,0.15)", color: "#5e6ad2" }}
                  >
                    {name[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate" style={{ color: "var(--lm-ink)" }}>{name}</p>
                    <p className="text-xs truncate" style={{ color: "var(--lm-ink-3)" }}>
                      {c?.email}
                    </p>
                  </div>
                  <span
                    className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0"
                    style={{ color: st.color, backgroundColor: `${st.color}18` }}
                  >
                    {st.label}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Divider */}
      <div style={{ width: "1px", backgroundColor: "var(--lm-border)", flexShrink: 0 }} />

      {/* Right — detail */}
      <div className="flex-1 min-w-0">
        {!selected ? (
          <div className="flex flex-col items-center justify-center h-full gap-3" style={{ color: "var(--lm-ink-3)" }}>
            <User className="h-10 w-10" />
            <p className="text-sm">{t("candidates.selectPrompt")}</p>
          </div>
        ) : (
          <CandidateDetail app={selected} />
        )}
      </div>
    </div>
    </>
  );
}

function CandidateDetail({ app }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const c = app.candidate;
  const st = { color: STAGE_COLORS[app.stage] || STAGE_COLORS.new, label: t(`recruitment.stages.${STAGE_COLORS[app.stage] ? app.stage : "new"}`) };
  const isHired = app.stage === "hired";
  const alreadyConverted = !!app.employee_id;
  const [noteText, setNoteText] = useState("");
  const noteInputRef = useRef(null);
  const [showConvertModal, setShowConvertModal] = useState(false);

  const { data: notes = [] } = useQuery({
    queryKey: ["application-notes", app.id],
    queryFn: () => recruitmentAPI.listNotes(app.id).then((r) => r.data),
  });

  const { data: stageHistory = [] } = useQuery({
    queryKey: ["stage-history", app.id],
    queryFn: () => recruitmentAPI.listStageHistory(app.id).then((r) => r.data),
  });

  const { data: posting } = useQuery({
    queryKey: ["posting", app.job_posting_id],
    queryFn: () => recruitmentAPI.getPosting(app.job_posting_id).then((r) => r.data),
    enabled: !!app.job_posting_id,
    staleTime: 300000,
  });
  const questionMap = Object.fromEntries((posting?.questions ?? []).map((q) => [String(q.id), q.label]));

  const addNoteMut = useMutation({
    mutationFn: (content) => recruitmentAPI.addNote(app.id, content),
    onSuccess: () => {
      setNoteText("");
      qc.invalidateQueries({ queryKey: ["application-notes", app.id] });
    },
  });

  const deleteNoteMut = useMutation({
    mutationFn: (noteId) => recruitmentAPI.deleteNote(app.id, noteId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["application-notes", app.id] });
    },
  });

  return (
    <div className="space-y-5">
      {/* Identity */}
      <div className="flex items-start gap-4">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-semibold flex-shrink-0"
          style={{ backgroundColor: "rgba(94,106,210,0.15)", color: "#5e6ad2" }}
        >
          {c ? `${c.first_name[0]}${c.last_name[0]}` : "?"}
        </div>
        <div>
          <h3 className="font-semibold text-base" style={{ color: "var(--lm-ink)" }}>
            {c ? `${c.first_name} ${c.last_name}` : t("candidates.detail.candidateFallback", { id: app.candidate_id })}
          </h3>
          <span
            className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full font-medium"
            style={{ color: st.color, backgroundColor: `${st.color}18` }}
          >
            {st.label}
          </span>
          {posting?.title && (
            <p className="mt-1 text-xs" style={{ color: "var(--lm-ink-3)" }}>
              {posting.title}
            </p>
          )}
        </div>
      </div>

      {/* Contact */}
      {c && (
        <div
          className="rounded-xl p-4 space-y-2"
          style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}
        >
          <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--lm-ink-3)" }}>{t("candidates.detail.contact")}</h4>
          {c.email && (
            <InfoRow icon={Mail} label={c.email} />
          )}
          {c.phone && (
            <InfoRow icon={Phone} label={c.phone} />
          )}
          {c.nationality && (
            <InfoRow icon={Globe} label={c.nationality} />
          )}
          {c.linkedin_url && (
            <InfoRow icon={Linkedin} label={c.linkedin_url} link={c.linkedin_url} />
          )}
        </div>
      )}

      {/* CV download */}
      {c?.cv_file_id && (
        <button
          onClick={async () => {
            const res = await careersAPI.downloadCV(c.cv_file_id);
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = c.cv_file_id;
            a.click();
            window.URL.revokeObjectURL(url);
          }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm w-full transition-colors"
          style={{ backgroundColor: "rgba(94,106,210,0.08)", border: "1px solid rgba(94,106,210,0.20)", color: "#5e6ad2" }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "rgba(94,106,210,0.15)"; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "rgba(94,106,210,0.08)"; }}
        >
          <Download className="h-4 w-4" />
          {t("candidates.detail.downloadCv")}
        </button>
      )}

      {/* Application info */}
      <div
        className="rounded-xl p-4 space-y-2"
        style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}
      >
        <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--lm-ink-3)" }}>{t("candidates.detail.application")}</h4>
        <InfoRow icon={Calendar} label={t("candidates.detail.appliedOn", { date: new Date(app.applied_at).toLocaleDateString(i18n.language, { day: "numeric", month: "long", year: "numeric" }) })} />
        {c?.source && (
          <InfoRow icon={Globe} label={t("candidates.detail.sourcePrefix", { source: c.source })} />
        )}
        {app.cover_letter && (
          <div className="mt-3">
            <p className="text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{t("candidates.detail.coverLetter")}</p>
            <p className="text-xs leading-relaxed" style={{ color: "var(--lm-ink-2)" }}>
              {app.cover_letter}
            </p>
          </div>
        )}
        {app.custom_answers && Object.keys(app.custom_answers).length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("candidates.detail.answers")}</p>
            {Object.entries(app.custom_answers).map(([qId, answer]) => (
              <div key={qId} className="rounded-lg px-3 py-2" style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}>
                <p className="text-xs font-medium mb-0.5" style={{ color: "var(--lm-ink-3)" }}>
                  {questionMap[qId] || t("candidates.detail.questionFallback", { id: qId })}
                </p>
                <p className="text-xs" style={{ color: "var(--lm-ink)" }}>{String(answer)}</p>
              </div>
            ))}
          </div>
        )}
        {app.rejection_reason && (
          <div className="mt-2 px-3 py-2 rounded-lg" style={{ backgroundColor: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.20)" }}>
            <p className="text-xs" style={{ color: "#dc2626" }}>{t("candidates.detail.rejectionReason", { reason: app.rejection_reason })}</p>
          </div>
        )}
      </div>

      {/* Stage changer */}
      <StageChanger app={app} />

      {/* Interviews */}
      <InterviewsSection app={app} />

      {/* Convert to employee — shown only for hired candidates */}
      {isHired && (
        <div
          className="rounded-xl p-4"
          style={{
            backgroundColor: alreadyConverted ? "rgba(22,163,74,0.06)" : "rgba(94,106,210,0.06)",
            border: `1px solid ${alreadyConverted ? "rgba(22,163,74,0.20)" : "rgba(94,106,210,0.20)"}`,
          }}
        >
          {alreadyConverted ? (
            <div className="flex items-center gap-2">
              <UserCheck className="h-4 w-4" style={{ color: "#16a34a" }} />
              <span className="text-xs font-medium" style={{ color: "#16a34a" }}>
                {t("candidates.detail.converted", { id: app.employee_id })}
              </span>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-medium" style={{ color: "var(--lm-ink)" }}>{t("candidates.detail.createEmployee")}</p>
                <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>
                  {t("candidates.detail.convertHint")}
                </p>
              </div>
              <button
                onClick={() => setShowConvertModal(true)}
                className="flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium"
                style={{ backgroundColor: "#5e6ad2", color: "#fff" }}
              >
                <UserCheck className="h-3.5 w-3.5" />
                {t("candidates.detail.convert")}
              </button>
            </div>
          )}
        </div>
      )}

      {showConvertModal && (
        <ConvertModal
          app={app}
          onClose={() => setShowConvertModal(false)}
          onConverted={() => {
            setShowConvertModal(false);
            qc.invalidateQueries({ queryKey: ["applications"] });
          }}
        />
      )}

      {/* Historique des stades */}
      {stageHistory.length > 0 && (
        <div
          className="rounded-xl p-4 space-y-3"
          style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}
        >
          <div className="flex items-center gap-2">
            <GitBranch className="h-3.5 w-3.5" style={{ color: "var(--lm-ink-3)" }} />
            <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--lm-ink-3)" }}>
              {t("candidates.detail.historyTitle")}
            </h4>
          </div>
          <div className="relative pl-4">
            {stageHistory.map((h, i) => {
              const hStage = STAGE_COLORS[h.stage?.toLowerCase()] ? h.stage.toLowerCase() : "new";
              const st = { color: STAGE_COLORS[hStage], label: t(`recruitment.stages.${hStage}`) };
              const isLast = i === stageHistory.length - 1;
              return (
                <div key={h.id} className="relative pb-3 last:pb-0">
                  {/* Vertical line */}
                  {!isLast && (
                    <div
                      className="absolute left-[-9px] top-[18px] w-px"
                      style={{ height: "calc(100% - 4px)", backgroundColor: "var(--lm-border)" }}
                    />
                  )}
                  {/* Dot */}
                  <div
                    className="absolute left-[-13px] top-[5px] w-2 h-2 rounded-full border-2"
                    style={{ backgroundColor: st.color, borderColor: "var(--lm-s2)" }}
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    <span
                      className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ color: st.color, backgroundColor: `${st.color}18` }}
                    >
                      {st.label}
                    </span>
                    <span className="text-xs" style={{ color: "var(--lm-ink-3)" }}>
                      {new Date(h.changed_at).toLocaleDateString(i18n.language, { day: "numeric", month: "short", year: "numeric" })}
                      {" · "}
                      {h.changed_by_name}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Notes internes */}
      <div
        className="rounded-xl p-4 space-y-3"
        style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}
      >
        <div className="flex items-center gap-2">
          <MessageSquare className="h-3.5 w-3.5" style={{ color: "var(--lm-ink-3)" }} />
          <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--lm-ink-3)" }}>
            {t("candidates.detail.notesTitle")}
          </h4>
          {notes.length > 0 && (
            <span
              className="text-xs px-1.5 py-0.5 rounded-full"
              style={{ backgroundColor: "rgba(94,106,210,0.12)", color: "#5e6ad2" }}
            >
              {notes.length}
            </span>
          )}
        </div>

        {/* Existing notes */}
        {notes.length === 0 ? (
          <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{t("candidates.notes.empty")}</p>
        ) : (
          <div className="space-y-2">
            {notes.map((note) => (
              <div
                key={note.id}
                className="rounded-lg px-3 py-2.5 flex gap-2"
                style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-xs font-medium" style={{ color: "var(--lm-ink)" }}>
                      {note.author_name || t("candidates.detail.noteAuthorDefault")}
                    </span>
                    <span className="text-xs" style={{ color: "var(--lm-ink-3)" }}>
                      · {new Date(note.created_at).toLocaleDateString(i18n.language, { day: "numeric", month: "short", year: "numeric" })}
                    </span>
                  </div>
                  <p className="text-xs leading-relaxed whitespace-pre-wrap" style={{ color: "var(--lm-ink-2)" }}>
                    {note.content}
                  </p>
                </div>
                <button
                  onClick={() => deleteNoteMut.mutate(note.id)}
                  disabled={deleteNoteMut.isPending}
                  className="flex-shrink-0 p-1 rounded transition-colors disabled:opacity-40"
                  style={{ color: "var(--lm-ink-3)" }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = "#dc2626"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = "var(--lm-ink-3)"; }}
                  title={t("candidates.notes.deleteNote")}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add note */}
        <div className="flex gap-2 pt-1">
          <textarea
            ref={noteInputRef}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && noteText.trim()) {
                addNoteMut.mutate(noteText.trim());
              }
            }}
            placeholder={t("candidates.notes.placeholder")}
            rows={2}
            className="flex-1 px-3 py-2 rounded-lg text-xs resize-none"
            style={{
              backgroundColor: "var(--lm-s1)",
              border: "1px solid var(--lm-border)",
              color: "var(--lm-ink)",
              outline: "none",
            }}
            onFocus={(e) => { e.target.style.border = "1px solid rgba(94,106,210,0.5)"; }}
            onBlur={(e) => { e.target.style.border = "1px solid var(--lm-border)"; }}
          />
          <button
            onClick={() => { if (noteText.trim()) addNoteMut.mutate(noteText.trim()); }}
            disabled={!noteText.trim() || addNoteMut.isPending}
            className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg self-end transition-all disabled:opacity-40"
            style={{ backgroundColor: "#5e6ad2", color: "#fff" }}
          >
            <Send className="h-3.5 w-3.5" />
          </button>
        </div>
        {addNoteMut.isError && (
          <p className="text-xs" style={{ color: "#dc2626" }}>
            {addNoteMut.error?.response?.data?.detail || t("candidates.notes.addError")}
          </p>
        )}
      </div>

      {/* GDPR */}
      {c && (
        <div className="flex items-center gap-2">
          <div
            className="w-4 h-4 rounded-sm flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: c.gdpr_consent ? "#16a34a" : "var(--lm-s2)", border: "1px solid var(--lm-border)" }}
          >
            {c.gdpr_consent && <span style={{ color: "#fff", fontSize: "10px" }}>✓</span>}
          </div>
          <span className="text-xs" style={{ color: "var(--lm-ink-3)" }}>
            {t("candidates.detail.gdprConsent")} {c.gdpr_consent ? t("candidates.detail.gdprGranted") : t("candidates.detail.gdprNotGranted")}
            {c.gdpr_consent_date && ` ${t("candidates.detail.gdprOn", { date: new Date(c.gdpr_consent_date).toLocaleDateString(i18n.language) })}`}
          </span>
        </div>
      )}
    </div>
  );
}

const INTERVIEW_TYPE_ICONS = {
  video:  Video,
  phone:  Phone,
  onsite: Building2,
};

const INTERVIEW_STATUS_COLORS = {
  scheduled: "#5e6ad2",
  completed:  "#16a34a",
  cancelled:  "#6b7280",
};

const EMPTY_FORM = { type: "video", scheduled_at: "", location_or_link: "" };

function InterviewsSection({ app }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [evaluatingId, setEvaluatingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [evalForm, setEvalForm] = useState({ evaluation_score: 3, evaluation_notes: "" });

  const { data: interviews = [] } = useQuery({
    queryKey: ["interviews", app.id],
    queryFn: () => recruitmentAPI.listInterviews({ application_id: app.id }).then((r) => r.data),
  });

  const createMut = useMutation({
    mutationFn: (data) => recruitmentAPI.createInterview(data),
    onSuccess: () => {
      setShowForm(false);
      setForm(EMPTY_FORM);
      qc.invalidateQueries({ queryKey: ["interviews", app.id] });
    },
  });

  const cancelMut = useMutation({
    mutationFn: (id) => recruitmentAPI.updateInterview(id, { status: "cancelled" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["interviews", app.id] }),
  });

  const evalMut = useMutation({
    mutationFn: ({ id, data }) => recruitmentAPI.evaluateInterview(id, data),
    onSuccess: () => {
      setEvaluatingId(null);
      setEvalForm({ evaluation_score: 3, evaluation_notes: "" });
      qc.invalidateQueries({ queryKey: ["interviews", app.id] });
    },
  });

  const fmt = (dt) =>
    dt
      ? new Date(dt).toLocaleString(i18n.language, { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
      : "—";

  return (
    <div
      className="rounded-xl p-4 space-y-3"
      style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="h-3.5 w-3.5" style={{ color: "var(--lm-ink-3)" }} />
          <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--lm-ink-3)" }}>
            {t("candidates.interview.title")}
          </h4>
          {interviews.length > 0 && (
            <span
              className="text-xs px-1.5 py-0.5 rounded-full"
              style={{ backgroundColor: "rgba(94,106,210,0.12)", color: "#5e6ad2" }}
            >
              {interviews.length}
            </span>
          )}
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg"
            style={{
              backgroundColor: "rgba(94,106,210,0.10)",
              color: "#5e6ad2",
              border: "1px solid rgba(94,106,210,0.25)",
            }}
          >
            <Plus className="h-3 w-3" />
            {t("candidates.interview.schedule")}
          </button>
        )}
      </div>

      {/* Interview list */}
      {interviews.length === 0 && !showForm && (
        <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{t("candidates.interview.empty")}</p>
      )}
      {interviews.map((iv) => {
        const TypeIcon = INTERVIEW_TYPE_ICONS[iv.type] || INTERVIEW_TYPE_ICONS.video;
        const typeLabel = t(`candidates.interview.types.${iv.type}`, { defaultValue: iv.type });
        const statusColor = INTERVIEW_STATUS_COLORS[iv.status] || INTERVIEW_STATUS_COLORS.scheduled;
        const statusLabel = t(`candidates.interview.status.${iv.status}`, { defaultValue: iv.status });
        const isEvaluating = evaluatingId === iv.id;

        return (
          <div
            key={iv.id}
            className="rounded-lg p-3 space-y-2"
            style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                <TypeIcon className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "var(--lm-ink-3)" }} />
                <div>
                  <span className="text-xs font-medium" style={{ color: "var(--lm-ink)" }}>{typeLabel}</span>
                  <span className="text-xs ml-2" style={{ color: "var(--lm-ink-3)" }}>{fmt(iv.scheduled_at)}</span>
                </div>
              </div>
              <span
                className="text-xs px-2 py-0.5 rounded-full flex-shrink-0"
                style={{ color: statusColor, backgroundColor: `${statusColor}18` }}
              >
                {statusLabel}
              </span>
            </div>

            {iv.location_or_link && (
              <p className="text-xs truncate" style={{ color: "var(--lm-ink-3)" }}>{iv.location_or_link}</p>
            )}

            {iv.evaluation_score > 0 && (
              <div className="flex items-center gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <Star
                    key={n}
                    className="h-3 w-3"
                    style={{
                      color: n <= iv.evaluation_score ? "#d97706" : "var(--lm-border)",
                      fill: n <= iv.evaluation_score ? "#d97706" : "transparent",
                    }}
                  />
                ))}
                {iv.evaluation_notes && (
                  <span className="text-xs ml-1" style={{ color: "var(--lm-ink-3)" }}>{iv.evaluation_notes}</span>
                )}
              </div>
            )}

            {iv.status === "scheduled" && !isEvaluating && (
              <div className="flex gap-2 pt-0.5">
                <button
                  onClick={() => {
                    setEvaluatingId(iv.id);
                    setEvalForm({ evaluation_score: 3, evaluation_notes: "" });
                  }}
                  className="text-xs px-2.5 py-1 rounded-lg"
                  style={{
                    backgroundColor: "rgba(94,106,210,0.10)",
                    color: "#5e6ad2",
                    border: "1px solid rgba(94,106,210,0.25)",
                  }}
                >
                  {t("candidates.interview.evaluate")}
                </button>
                <button
                  onClick={() => cancelMut.mutate(iv.id)}
                  disabled={cancelMut.isPending}
                  className="text-xs px-2.5 py-1 rounded-lg disabled:opacity-50"
                  style={{
                    backgroundColor: "rgba(107,114,128,0.10)",
                    color: "#6b7280",
                    border: "1px solid rgba(107,114,128,0.25)",
                  }}
                >
                  {t("common.cancel")}
                </button>
              </div>
            )}

            {isEvaluating && (
              <div className="space-y-2 pt-1">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setEvalForm((f) => ({ ...f, evaluation_score: n }))}
                      className="p-0.5"
                    >
                      <Star
                        className="h-4 w-4"
                        style={{
                          color: n <= evalForm.evaluation_score ? "#d97706" : "var(--lm-border)",
                          fill: n <= evalForm.evaluation_score ? "#d97706" : "transparent",
                          cursor: "pointer",
                        }}
                      />
                    </button>
                  ))}
                  <span className="text-xs ml-1" style={{ color: "var(--lm-ink-3)" }}>
                    {evalForm.evaluation_score}/5
                  </span>
                </div>
                <textarea
                  rows={2}
                  value={evalForm.evaluation_notes}
                  onChange={(e) => setEvalForm((f) => ({ ...f, evaluation_notes: e.target.value }))}
                  placeholder={t("candidates.interview.evalPlaceholder")}
                  className="w-full px-3 py-2 rounded-lg text-xs resize-none"
                  style={{
                    backgroundColor: "var(--lm-s2)",
                    border: "1px solid var(--lm-border)",
                    color: "var(--lm-ink)",
                    outline: "none",
                  }}
                />
                {evalMut.isError && (
                  <p className="text-xs" style={{ color: "#dc2626" }}>
                    {evalMut.error?.response?.data?.detail || t("candidates.interview.evalError")}
                  </p>
                )}
                <div className="flex gap-2">
                  <button
                    onClick={() => evalMut.mutate({ id: iv.id, data: evalForm })}
                    disabled={evalMut.isPending || evalForm.evaluation_score === 0}
                    className="text-xs px-3 py-1.5 rounded-lg font-medium disabled:opacity-60"
                    style={{ backgroundColor: "#5e6ad2", color: "#fff" }}
                  >
                    {evalMut.isPending ? t("candidates.add.creating") : t("common.save")}
                  </button>
                  <button
                    onClick={() => setEvaluatingId(null)}
                    className="text-xs px-3 py-1.5 rounded-lg"
                    style={{
                      backgroundColor: "var(--lm-s2)",
                      color: "var(--lm-ink-2)",
                      border: "1px solid var(--lm-border)",
                    }}
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* New interview form */}
      {showForm && (
        <div
          className="rounded-lg p-3 space-y-2.5"
          style={{ backgroundColor: "var(--lm-s1)", border: "1px solid rgba(94,106,210,0.25)" }}
        >
          {/* Type selector */}
          <div className="flex gap-1.5 flex-wrap">
            {Object.entries(INTERVIEW_TYPE_ICONS).map(([val, TypeIcon]) => {
              const active = form.type === val;
              return (
                <button
                  key={val}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, type: val }))}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs transition-all"
                  style={{
                    backgroundColor: active ? "rgba(94,106,210,0.15)" : "transparent",
                    border: `1px solid ${active ? "rgba(94,106,210,0.40)" : "var(--lm-border)"}`,
                    color: active ? "#5e6ad2" : "var(--lm-ink-3)",
                  }}
                >
                  <TypeIcon className="h-3 w-3" />
                  {t(`candidates.interview.types.${val}`)}
                </button>
              );
            })}
          </div>

          <input
            type="datetime-local"
            value={form.scheduled_at}
            onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg text-xs"
            style={{
              backgroundColor: "var(--lm-s2)",
              border: "1px solid var(--lm-border)",
              color: "var(--lm-ink)",
              outline: "none",
            }}
          />

          <input
            type="text"
            value={form.location_or_link}
            onChange={(e) => setForm((f) => ({ ...f, location_or_link: e.target.value }))}
            placeholder={t("candidates.interview.linkPlaceholder")}
            className="w-full px-3 py-2 rounded-lg text-xs"
            style={{
              backgroundColor: "var(--lm-s2)",
              border: "1px solid var(--lm-border)",
              color: "var(--lm-ink)",
              outline: "none",
            }}
          />

          {createMut.isError && (
            <p className="text-xs" style={{ color: "#dc2626" }}>
              {createMut.error?.response?.data?.detail || t("candidates.interview.createError")}
            </p>
          )}

          <div className="flex gap-2">
            <button
              onClick={() =>
                createMut.mutate({
                  application_id: app.id,
                  type: form.type,
                  scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null,
                  location_or_link: form.location_or_link || null,
                })
              }
              disabled={createMut.isPending}
              className="text-xs px-3 py-1.5 rounded-lg font-medium disabled:opacity-60"
              style={{ backgroundColor: "#5e6ad2", color: "#fff" }}
            >
              {createMut.isPending ? t("candidates.add.creating") : t("candidates.interview.schedule")}
            </button>
            <button
              onClick={() => setShowForm(false)}
              className="text-xs px-3 py-1.5 rounded-lg"
              style={{
                backgroundColor: "var(--lm-s2)",
                color: "var(--lm-ink-2)",
                border: "1px solid var(--lm-border)",
              }}
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StageChanger({ app }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const moveMut = useMutation({
    mutationFn: ({ stage, reason }) => {
      if (stage === "rejected") return recruitmentAPI.rejectApplication(app.id, reason || null);
      if (stage === "hired") return recruitmentAPI.hireApplication(app.id);
      return recruitmentAPI.moveStage(app.id, stage);
    },
    onSuccess: () => {
      setShowRejectInput(false);
      setRejectReason("");
      qc.invalidateQueries({ queryKey: ["applications", "all"] });
      qc.invalidateQueries({ queryKey: ["stage-history", app.id] });
    },
  });

  const stages = ["new", "screening", "interview", "offer", "hired", "rejected"];

  return (
    <div
      className="rounded-xl p-4 space-y-3"
      style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}
    >
      <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--lm-ink-3)" }}>
        {t("candidates.stage.changeTitle")}
      </h4>
      <div className="flex flex-wrap gap-1.5">
        {stages.map((s) => {
          const info = { color: STAGE_COLORS[s] };
          const isCurrent = app.stage === s;
          return (
            <button
              key={s}
              disabled={moveMut.isPending || isCurrent}
              onClick={() => {
                if (isCurrent) return;
                if (s === "rejected") { setShowRejectInput(true); return; }
                moveMut.mutate({ stage: s });
              }}
              className="text-xs px-2.5 py-1 rounded-full transition-all disabled:opacity-50"
              style={{
                color: isCurrent ? "#fff" : info.color,
                backgroundColor: isCurrent ? info.color : `${info.color}18`,
                border: `1px solid ${isCurrent ? info.color : `${info.color}40`}`,
                fontWeight: isCurrent ? 600 : 400,
                cursor: isCurrent ? "default" : "pointer",
              }}
            >
              {t(`recruitment.stages.${s}`)}
            </button>
          );
        })}
      </div>
      {showRejectInput && (
        <div className="space-y-2">
          <input
            type="text"
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder={t("candidates.stage.rejectPlaceholder")}
            className="w-full px-3 py-2 rounded-lg text-xs"
            style={{
              backgroundColor: "var(--lm-s1)",
              border: "1px solid var(--lm-border)",
              color: "var(--lm-ink)",
              outline: "none",
            }}
          />
          <div className="flex gap-2">
            <button
              onClick={() => moveMut.mutate({ stage: "rejected", reason: rejectReason })}
              disabled={moveMut.isPending}
              className="px-3 py-1.5 rounded-lg text-xs font-medium disabled:opacity-60"
              style={{ backgroundColor: "#dc2626", color: "#fff" }}
            >
              {moveMut.isPending ? t("candidates.add.creating") : t("candidates.stage.confirmReject")}
            </button>
            <button
              onClick={() => { setShowRejectInput(false); setRejectReason(""); }}
              className="px-3 py-1.5 rounded-lg text-xs"
              style={{ backgroundColor: "var(--lm-s1)", color: "var(--lm-ink-2)", border: "1px solid var(--lm-border)" }}
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
      {moveMut.isError && (
        <p className="text-xs" style={{ color: "#dc2626" }}>
          {moveMut.error?.response?.data?.detail || t("candidates.stage.changeError")}
        </p>
      )}
    </div>
  );
}

const CONTRACT_VALUES = ["PERMANENT", "TEMPORARY", "INTERNSHIP", "FREELANCE"];

function ConvertModal({ app, onClose, onConverted }) {
  const { t } = useTranslation();
  const c = app.candidate;
  const today = new Date().toISOString().split("T")[0];

  const [form, setForm] = useState({
    department_id: "",
    site_id: "",
    contract_type: "PERMANENT",
    hire_date: today,
  });

  const { data: orgData } = useQuery({
    queryKey: ["org-chart-simple"],
    queryFn: () => recruitmentAPI.listDepartments().then((r) => r.data),
  });

  const { data: sitesData } = useQuery({
    queryKey: ["sites-simple"],
    queryFn: () => recruitmentAPI.listSites().then((r) => r.data),
  });

  const departments = (() => {
    if (!orgData?.organization_chart) return [];
    const flatten = (nodes, prefix = "") => {
      let out = [];
      for (const n of nodes) {
        out.push({ id: n.id, name: prefix ? `${prefix} › ${n.nom}` : n.nom });
        if (n.enfants?.length) out = out.concat(flatten(n.enfants, prefix ? `${prefix} › ${n.nom}` : n.nom));
      }
      return out;
    };
    return flatten(orgData.organization_chart);
  })();

  const sites = Array.isArray(sitesData) ? sitesData : (sitesData?.sites || []);

  const convertMut = useMutation({
    mutationFn: () => recruitmentAPI.convertToEmployee(app.id, {
      department_id: form.department_id ? parseInt(form.department_id) : null,
      site_id: form.site_id ? parseInt(form.site_id) : null,
      contract_type: form.contract_type || null,
      hire_date: form.hire_date ? new Date(form.hire_date).toISOString() : null,
    }),
    onSuccess: onConverted,
  });

  const field = (label, children) => (
    <div>
      <label className="block text-xs font-medium mb-1" style={{ color: "var(--lm-ink-2)" }}>{label}</label>
      {children}
    </div>
  );

  const selectStyle = {
    width: "100%",
    padding: "6px 10px",
    borderRadius: "8px",
    fontSize: "12px",
    backgroundColor: "var(--lm-s1)",
    border: "1px solid var(--lm-border)",
    color: "var(--lm-ink)",
    outline: "none",
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.55)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl shadow-2xl flex flex-col"
        style={{ backgroundColor: "var(--lm-s0)", border: "1px solid var(--lm-border)", maxHeight: "90vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: "1px solid var(--lm-border)" }}>
          <div className="flex items-center gap-2">
            <UserCheck className="h-4 w-4" style={{ color: "#5e6ad2" }} />
            <h2 className="text-sm font-semibold" style={{ color: "var(--lm-ink)" }}>{t("candidates.convert.title")}</h2>
          </div>
          <button onClick={onClose} className="p-1 rounded-lg transition-colors" style={{ color: "var(--lm-ink-3)" }}>
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Pre-filled identity */}
          <div
            className="rounded-xl p-3 space-y-1.5"
            style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}
          >
            <p className="text-xs font-semibold" style={{ color: "var(--lm-ink-3)" }}>{t("candidates.convert.identity")}</p>
            <p className="text-sm font-medium" style={{ color: "var(--lm-ink)" }}>
              {c?.first_name} {c?.last_name}
            </p>
            {c?.email && <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{c.email}</p>}
            {c?.phone && <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{c.phone}</p>}
          </div>

          {/* Editable fields */}
          {field(t("candidates.convert.department"), (
            <select
              value={form.department_id}
              onChange={(e) => setForm((f) => ({ ...f, department_id: e.target.value }))}
              style={selectStyle}
            >
              <option value="">{t("candidates.convert.noneOption")}</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          ))}

          {field(t("candidates.convert.site"), (
            <select
              value={form.site_id}
              onChange={(e) => setForm((f) => ({ ...f, site_id: e.target.value }))}
              style={selectStyle}
            >
              <option value="">{t("candidates.convert.noneOption")}</option>
              {sites.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          ))}

          {field(t("candidates.convert.contractType"), (
            <select
              value={form.contract_type}
              onChange={(e) => setForm((f) => ({ ...f, contract_type: e.target.value }))}
              style={selectStyle}
            >
              {CONTRACT_VALUES.map((v) => (
                <option key={v} value={v}>{t(`candidates.contract.${v}`)}</option>
              ))}
            </select>
          ))}

          {field(t("candidates.convert.hireDate"), (
            <input
              type="date"
              value={form.hire_date}
              onChange={(e) => setForm((f) => ({ ...f, hire_date: e.target.value }))}
              style={selectStyle}
            />
          ))}

          {convertMut.isError && (
            <p className="text-xs px-3 py-2 rounded-lg" style={{ color: "#dc2626", backgroundColor: "rgba(220,38,38,0.08)" }}>
              {convertMut.error?.response?.data?.detail || t("candidates.convert.error")}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4" style={{ borderTop: "1px solid var(--lm-border)" }}>
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-xs"
            style={{ backgroundColor: "var(--lm-s2)", color: "var(--lm-ink-2)", border: "1px solid var(--lm-border)" }}
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={() => convertMut.mutate()}
            disabled={convertMut.isPending}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium disabled:opacity-60"
            style={{ backgroundColor: "#5e6ad2", color: "#fff" }}
          >
            <UserCheck className="h-3.5 w-3.5" />
            {convertMut.isPending ? t("candidates.convert.creating") : t("candidates.convert.create")}
          </button>
        </div>
      </div>
    </div>
  );
}


function InfoRow({ icon: Icon, label, link }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-3.5 w-3.5 flex-shrink-0" style={{ color: "var(--lm-ink-3)" }} />
      {link ? (
        <a href={link} target="_blank" rel="noopener noreferrer" className="text-xs truncate" style={{ color: "#5e6ad2" }}>
          {label}
        </a>
      ) : (
        <span className="text-xs truncate" style={{ color: "var(--lm-ink-2)" }}>{label}</span>
      )}
    </div>
  );
}
